import React from "react";
import DemoForm from "../components/DemoForm";
import { CheckCircle2, ShieldCheck, Network, Lock, CalendarClock } from "lucide-react";
import SEO, { breadcrumb } from "../components/SEO";

const CALENDLY_URL = "https://calendly.com/ohmyvue/30min";

export default function DemoPage() {
  return (
    <div className="bg-[var(--mc-navy-950)] min-h-screen" data-testid="page-demo">
      <SEO
        title="Planifier une démo MetaConnect (30 min)"
        description="Réservez une démo de 30 min : tour produit, modules adaptés à votre cas d'usage, intégration avec INS, DMP, MSSanté et Pro Santé Connect."
        canonical="/demo"
        schema={breadcrumb([
          { name: "Accueil", url: "/" },
          { name: "Démo", url: "/demo" },
        ])}
      />
      <section className="relative hex-grid pt-32 pb-20 lg:pt-40 lg:pb-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#0369A1]">
              <span className="h-px w-7 bg-[var(--mc-blue-400)]" />
              Démo guidée
            </div>
            <h1 className="mt-4 font-display text-[#0F2B5B] text-4xl sm:text-5xl font-semibold leading-[1.05]">
              Planifiez une <span className="text-[#0369A1]">démo</span> personnalisée
            </h1>
            <p className="mt-5 text-[15px] text-[#475569] leading-relaxed">
              30 minutes pour découvrir l'architecture MetaConnect, les modules adaptés à
              votre cas d'usage, et les modalités d'intégration avec votre écosystème.
            </p>
            <ul className="mt-8 grid gap-3">
              {[
                "Tour produit live (modules, parcours, intégrations)",
                "Cas d'usage personnalisés selon votre profil",
                "Conformité, sécurité et hébergement HDS",
                "Estimation de planning de mise en place",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-[14px] text-[#334155]">
                  <CheckCircle2 size={16} className="mt-0.5 text-[var(--mc-blue-400)] shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-10 grid grid-cols-3 gap-3">
              {[
                { icon: <ShieldCheck size={16} />, t: "HDS" },
                { icon: <Lock size={16} />, t: "RGPD" },
                { icon: <Network size={16} />, t: "INS · DMP" },
              ].map((b) => (
                <div key={b.t} className="logo-tile flex items-center justify-center gap-2 text-[12px]">
                  {b.icon}{b.t}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-7 space-y-8">
            {/* Calendly direct booking */}
            <div className="mc-card p-7 lg:p-9" data-testid="calendly-card">
              <div className="flex items-start gap-4">
                <div className="grid place-items-center h-11 w-11 rounded-lg bg-[rgba(45,123,255,0.15)] border border-[rgba(127,179,255,0.28)] text-[#0369A1] shrink-0">
                  <CalendarClock size={22} />
                </div>
                <div className="flex-1">
                  <h2 className="font-display text-[#0F2B5B] text-2xl">Choisir un créneau directement</h2>
                  <p className="mt-2 text-[13.5px] text-[#475569] leading-relaxed">
                    Réservez un créneau de 30 minutes en direct avec notre équipe via Calendly.
                  </p>
                </div>
              </div>

              <div className="mt-6 rounded-xl overflow-hidden border border-slate-200" style={{ background: "white" }}>
                <iframe
                  src={`${CALENDLY_URL}?hide_gdpr_banner=1&background_color=ffffff&text_color=0A1A33&primary_color=2D7BFF`}
                  title="Réserver une démo MetaConnect"
                  data-testid="calendly-iframe"
                  className="w-full"
                  style={{ height: 720, border: 0 }}
                />
              </div>

              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="calendly-fallback-link"
                className="mt-4 inline-flex items-center gap-2 text-[13px] text-[#0369A1] link-underline"
              >
                Ouvrir Calendly dans un nouvel onglet ↗
              </a>
            </div>

            {/* Form fallback */}
            <div className="mc-card p-7 lg:p-9">
              <div className="flex items-center gap-3">
                <div className="h-px flex-1 bg-[rgba(127,179,255,0.18)]" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#475569]">Ou par message</span>
                <div className="h-px flex-1 bg-[rgba(127,179,255,0.18)]" />
              </div>
              <h2 className="mt-6 font-display text-[#0F2B5B] text-2xl">Décrivez votre projet</h2>
              <p className="mt-2 text-[13px] text-[#475569]">
                Notre équipe vous recontacte sous 48h pour confirmer le créneau.
              </p>
              <div className="mt-7">
                <DemoForm />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
